import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AppSettings } from '@services/settings/app-settings.service';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { MailHistoryItem, MailManagementService } from '../../services/mail-management.service';
import { MailListComponent } from '../../components/mail-list/mail-list.component';
import { MailDetailComponent } from '../../components/mail-detail/mail-detail.component';

@Component({
  selector: 'app-mail-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageHeaderComponent,
    MailListComponent,
    MailDetailComponent
  ],
  templateUrl: './mail-management.component.html',
  styleUrl: './mail-management.component.scss'
})
export class MailManagementComponent implements OnInit {
  readonly appSettings = inject(AppSettings);
  private mailService = inject(MailManagementService);

  public items = signal<MailHistoryItem[]>([]);
  public selectedMail = signal<MailHistoryItem | null>(null);

  // Titulo dinamico para la cabecera segun seleccion y vista movil
  public pageHeaderTitle = computed<string>(() => {
    const isMobile = this.appSettings.isMobile();
    const mail = this.selectedMail();
    return isMobile && mail ? (mail.subject || 'Detalle del Correo') : 'Historial de Correos';
  });
  public loading = signal<boolean>(false);
  public loadingMore = signal<boolean>(false);
  public page = signal<number>(1);
  public pages = signal<number>(1);
  public total = signal<number>(0);
  public limit = 20;

  public searchQuery = signal<string>('');
  public activeStatus = signal<string | null>(null);

  ngOnInit(): void {
    this.loadData(false);
  }

  // Carga lista de correos con paginacion o scroll continuo
  loadData(append = false): void {
    if (append) this.loadingMore.set(true);
    else this.loading.set(true);
    const filters = this._buildFilters();
    this.mailService.getHistory(filters).subscribe({
      next: (res) => this._handleResponse(res, append),
      error: () => { this.loading.set(false); this.loadingMore.set(false); }
    });
  }

  // Construye filtros para la peticion de historial
  private _buildFilters(): Record<string, any> {
    return {
      page: this.page(),
      limit: this.limit,
      search: this.searchQuery().trim() || undefined,
      status: this.activeStatus() ?? undefined
    };
  }

  // Procesa respuesta y actualiza listado o anexa items
  private _handleResponse(res: any, append: boolean): void {
    const newItems = res?.items ?? [];
    if (append) {
      this.items.update(prev => [...prev, ...newItems]);
    } else {
      this.items.set(newItems);
      this._autoSelectFirst(newItems);
    }
    this.total.set(res?.total ?? 0);
    this.pages.set(res?.pages ?? 1);
    this.loading.set(false);
    this.loadingMore.set(false);
  }

  // Selecciona primer correo en escritorio si no hay seleccion
  private _autoSelectFirst(list: MailHistoryItem[]): void {
    if (!this.appSettings.isMobile() && !this.selectedMail() && list.length > 0) {
      this.selectedMail.set(list[0]);
    }
  }

  // Solicita siguiente pagina al llegar al fondo del scroll
  onLoadMore(): void {
    if (this.loading() || this.loadingMore() || this.page() >= this.pages()) return;
    this.page.update(p => p + 1);
    this.loadData(true);
  }

  // Aplica filtro por texto de busqueda
  onSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.page.set(1);
    this.loadData(false);
  }

  // Aplica filtro por estado de envio
  onStatusChange(status: string | null): void {
    this.activeStatus.set(status);
    this.page.set(1);
    this.loadData(false);
  }

  // Selecciona un correo para mostrar en el detalle
  onSelectMail(item: MailHistoryItem): void {
    this.selectedMail.set(item);
  }

  // Regresa a la lista en vista movil
  onBackToList(): void {
    this.selectedMail.set(null);
  }

  // Recarga el listado tras completar un reenvio
  onMailResent(): void {
    this.loadData(false);
  }
}
